"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const LogEvent_1 = __importDefault(require("./LogEvent"));
const StatsigIdentity_1 = __importDefault(require("./StatsigIdentity"));
const StatsigLogger_1 = __importDefault(require("./StatsigLogger"));
const StatsigNetwork_1 = __importDefault(require("./StatsigNetwork"));
const StatsigSDKOptions_1 = __importDefault(require("./StatsigSDKOptions"));
const StatsigStore_1 = __importDefault(require("./StatsigStore"));
const Hashing_1 = require("./utils/Hashing");
const StatsigAsyncStorage_1 = __importDefault(require("./utils/StatsigAsyncStorage"));
const StatsigLocalStorage_1 = __importDefault(require("./utils/StatsigLocalStorage"));
const MAX_VALUE_SIZE = 64;
const MAX_OBJ_SIZE = 1024;
class StatsigClient {
    constructor(sdkKey, user, options) {
        this.appState = null;
        this.currentAppState = null;
        this.initCalled = false;
        this.pendingInitPromise = null;
        this.optionalLoggingSetup = false;
        if (typeof sdkKey !== 'string' || !sdkKey.startsWith('client-')) {
            throw new Error('Invalid key provided.  You must use a Client SDK Key from the Statsig console to initialize the sdk');
        }
        this.ready = false;
        this.sdkKey = sdkKey;
        this.options = new StatsigSDKOptions_1.default(options);
        this.identity = new StatsigIdentity_1.default(this.normalizeUser(user !== null && user !== void 0 ? user : null), this.options.getOverrideStableID(), StatsigClient.reactNativeUUID);
        this.network = new StatsigNetwork_1.default(this);
        this.store = new StatsigStore_1.default(this);
        this.logger = new StatsigLogger_1.default(this);
        if ((options === null || options === void 0 ? void 0 : options.initializeValues) != null) {
            this.setInitializeValues(options === null || options === void 0 ? void 0 : options.initializeValues);
        }
    }
    getNetwork() {
        return this.network;
    }
    getStore() {
        return this.store;
    }
    getLogger() {
        return this.logger;
    }
    getOptions() {
        return this.options;
    }
    getSDKKey() {
        if (this.sdkKey == null) {
            return '';
        }
        return this.sdkKey;
    }
    getCurrentUser() {
        return this.identity.getUser();
    }
    getCurrentUserCacheKey() {
        var _a, _b, _c;
        let key = `userID:${String((_b = (_a = this.identity.getUser()) === null || _a === void 0 ? void 0 : _a.userID) !== null && _b !== void 0 ? _b : '')};stableID:${this.getStableID()}`;
        const customIDs = (_c = this.identity.getUser()) === null || _c === void 0 ? void 0 : _c.customIDs;
        if (customIDs != null) {
            for (const [type, value] of Object.entries(customIDs)) {
                key += `;${type}:${value}`;
            }
        }
        return (0, Hashing_1.SimpleHash)(key);
    }
    getStatsigMetadata() {
        return this.identity.getStatsigMetadata();
    }
    setInitializeValues(initializeValues) {
        this.store.bootstrap(initializeValues);
        if (!this.ready) {
            // the sdk is usable and considered initialized when configured
            // with initializeValues
            this.ready = true;
            this.initCalled = true;
        }
        // we wont have access to window/document/localStorage if these run on the server
        // so try to run whenever this is called
        this.handleOptionalLogging();
        this.logger.sendSavedRequests();
    }
    async initializeAsync() {
        if (this.pendingInitPromise != null) {
            return this.pendingInitPromise;
        }
        if (this.ready) {
            return Promise.resolve();
        }
        this.initCalled = true;
        if (StatsigAsyncStorage_1.default.asyncStorage) {
            await this.identity.initAsync();
            await this.store.loadFromAsyncStorage();
        }
        if (this.appState &&
            this.appState.addEventListener &&
            typeof this.appState.addEventListener === 'function') {
            this.currentAppState = this.appState.currentState;
            this.appState.addEventListener('change', this.handleAppStateChange.bind(this));
        }
        const userCacheKey = this.getCurrentUserCacheKey();
        if (this.options.getLocalModeEnabled()) {
            return Promise.resolve();
        }
        this.pendingInitPromise = this.network
            .fetchValues(this.identity.getUser(), this.options.getInitTimeoutMs(), async (json) => {
            await this.store.save(userCacheKey, json);
            return;
        }, (e) => { })
            .catch((e) => { })
            .finally(async () => {
            this.pendingInitPromise = null;
            this.ready = true;
            this.logger.sendSavedRequests();
        });
        this.handleOptionalLogging();
        return this.pendingInitPromise;
    }
    /**
     * Checks the value of a gate for the current user
     * @param {string} gateName - the name of the gate to check
     * @param {boolean} ignoreOverrides = false if this check should ignore local overrides
     * @returns {boolean} - value of a gate for the user. Gates are "off" (return false) by default
     * @throws Error if initialize() is not called first, or gateName is not a string
     */
    checkGate(gateName, ignoreOverrides = false) {
        this.ensureStoreLoaded();
        if (typeof gateName !== 'string' || gateName.length === 0) {
            throw new Error('Must pass a valid string as the gateName.');
        }
        return this.store.checkGate(gateName, ignoreOverrides);
    }
    /**
     * Checks the value of a config for the current user
     * @param {string} configName - the name of the config to get
     * @param {boolean} ignoreOverrides = false if this check should ignore local overrides
     * @returns {DynamicConfig} - value of a config for the user
     * @throws Error if initialize() is not called first, or configName is not a string
     */
    getConfig(configName, ignoreOverrides = false) {
        this.ensureStoreLoaded();
        if (typeof configName !== 'string' || configName.length === 0) {
            throw new Error('Must pass a valid string as the configName.');
        }
        return this.store.getConfig(configName, ignoreOverrides);
    }
    /**
     * Gets the experiment for a given user
     * @param {string} experimentName - the name of the experiment to get
     * @param {boolean} keepDeviceValue = false if this should use "sticky" values persisted in local storage
     * @param {boolean} ignoreOverrides = false if this check should ignore local overrides
     * @returns {DynamicConfig} - value of the experiment for the user, represented by a Dynamic Config object
     * @throws Error if initialize() is not called first, or experimentName is not a string
     */
    getExperiment(experimentName, keepDeviceValue = false, ignoreOverrides = false) {
        this.ensureStoreLoaded();
        if (typeof experimentName !== 'string' || experimentName.length === 0) {
            throw new Error('Must pass a valid string as the experimentName.');
        }
        return this.store.getExperiment(experimentName, keepDeviceValue, ignoreOverrides);
    }
    getLayer(layerName, keepDeviceValue = false) {
        this.ensureStoreLoaded();
        if (typeof layerName !== 'string' || layerName.length === 0) {
            throw new Error('Must pass a valid string as the layerName.');
        }
        return this.store.getLayer(layerName, keepDeviceValue);
    }
    logEvent(eventName, value = null, metadata = null) {
        if (!this.logger || !this.sdkKey) {
            throw new Error('Must initialize() before logging events.');
        }
        if (typeof eventName !== 'string' || eventName.length === 0) {
            console.error('Event not logged. No valid eventName passed.');
            return;
        }
        if (this.shouldTrimParam(eventName, MAX_VALUE_SIZE)) {
            console.warn('eventName is too long, trimming to ' + MAX_VALUE_SIZE + ' characters.');
            eventName = eventName.substring(0, MAX_VALUE_SIZE);
        }
        if (typeof value === 'string' &&
            this.shouldTrimParam(value, MAX_VALUE_SIZE)) {
            console.warn('value is too long, trimming to ' + MAX_VALUE_SIZE + '.');
            value = value.substring(0, MAX_VALUE_SIZE);
        }
        if (this.shouldTrimParam(metadata, MAX_OBJ_SIZE)) {
            console.warn('metadata is too big. Dropping the metadata.');
            metadata = { error: 'not logged due to size too large' };
        }
        const event = new LogEvent_1.default(eventName);
        event.setValue(value);
        event.setMetadata(metadata);
        event.setUser(this.getCurrentUser());
        this.logger.log(event);
    }
    async updateUser(user) {
        if (!this.initializeCalled()) {
            throw new Error('Call initialize() first.');
        }
        this.identity.updateUser(this.normalizeUser(user));
        this.logger.resetDedupeKeys();
        const currentUser = this.identity.getUser();
        const userCacheKey = this.getCurrentUserCacheKey();
        if (this.pendingInitPromise != null) {
            await this.pendingInitPromise;
        }
        if (this.options.getLocalModeEnabled()) {
            return Promise.resolve(true);
        }
        this.pendingInitPromise = this.network
            .fetchValues(currentUser, this.options.getInitTimeoutMs(), async (json) => {
            await this.store.save(userCacheKey, json);
        }, (e) => { })
            .finally(() => {
            this.pendingInitPromise = null;
        });
        return this.pendingInitPromise
            .then(() => {
            return Promise.resolve(true);
        })
            .catch(() => {
            return Promise.resolve(false);
        });
    }
    /**
     * Informs the statsig SDK that the client is closing or shutting down
     * so the SDK can clean up internal state
     */
    shutdown() {
        this.logger.flush(true);
        if (this.appState &&
            this.appState.removeEventListener &&
            typeof this.appState.removeEventListener === 'function') {
            this.appState.removeEventListener('change', this.handleAppStateChange.bind(this));
        }
        StatsigLocalStorage_1.default.cleanup();
    }
    /**
     * Stores a local gate override
     * @param gateName the gate to override
     * @param value the value to override the gate to
     */
    overrideGate(gateName, value) {
        this.ensureStoreLoaded();
        this.store.overrideGate(gateName, value);
    }
    /**
     * Stores a local config override
     * @param gateName the config to override
     * @param value the json value to override the config to
     */
    overrideConfig(configName, value) {
        this.ensureStoreLoaded();
        this.store.overrideConfig(configName, value);
    }
    /**
     * Removes the given gate override
     * @param gateName
     */
    removeGateOverride(gateName) {
        this.ensureStoreLoaded();
        this.store.removeGateOverride(gateName);
    }
    /**
     * Removes the given config override
     * @param configName
     */
    removeConfigOverride(configName) {
        this.ensureStoreLoaded();
        this.store.removeConfigOverride(configName);
    }
    /**
     * @deprecated - use removeGateOverride or removeConfig override
     * Removes the given gate override
     * @param gateName
     */
    removeOverride(gateName) {
        this.ensureStoreLoaded();
        this.store.removeGateOverride(gateName);
    }
    /**
     * @deprecated - use getAllOverrides to get gate and config overrides
     * @returns Gate overrides
     */
    getOverrides() {
        this.ensureStoreLoaded();
        return this.store.getAllOverrides().gates;
    }
    /**
     * @returns The local gate and config overrides
     */
    getAllOverrides() {
        this.ensureStoreLoaded();
        return this.store.getAllOverrides();
    }
    /**
     * @returns The Statsig stable ID used for device level experiments
     */
    getStableID() {
        return this.identity.getStatsigMetadata().stableID;
    }
    initializeCalled() {
        return this.initCalled;
    }
    // All methods below are for the statsig react native SDK internal usage only!
    setSDKPackageInfo(sdkPackageInfo) {
        if (sdkPackageInfo != null) {
            this.identity.setSDKPackageInfo(sdkPackageInfo);
        }
    }
    static setAsyncStorage(asyncStorage) {
        if (asyncStorage != null) {
            StatsigAsyncStorage_1.default.asyncStorage = asyncStorage;
        }
    }
    static setReactNativeUUID(uuid) {
        if (uuid != null) {
            StatsigClient.reactNativeUUID = uuid;
        }
    }
    setAppState(appState) {
        if (appState != null) {
            this.appState = appState;
        }
    }
    setNativeModules(nativeModules) {
        if (nativeModules != null) {
            this.identity.setNativeModules(nativeModules);
        }
    }
    setPlatform(platform) {
        if (platform != null) {
            this.identity.setPlatform(platform);
        }
    }
    setRNDeviceInfo(deviceInfo) {
        if (deviceInfo != null) {
            this.identity.setRNDeviceInfo(deviceInfo);
        }
    }
    setExpoConstants(expoConstants) {
        if (expoConstants != null) {
            this.identity.setExpoConstants(expoConstants);
        }
    }
    setExpoDevice(expoDevice) {
        if (expoDevice != null) {
            this.identity.setExpoDevice(expoDevice);
        }
    }
    handleOptionalLogging() {
        if (typeof window === 'undefined' || !window || !window.addEventListener) {
            return;
        }
        if (this.optionalLoggingSetup) {
            return;
        }
        const user = this.identity.getUser();
        if (!this.options.getDisableErrorLogging()) {
            window.addEventListener('error', (e) => {
                var _a;
                this.logger.logAppError(user, (_a = e.message) !== null && _a !== void 0 ? _a : '', {
                    filename: e.filename,
                    lineno: e.lineno,
                    colno: e.colno,
                    error_obj: e.error,
                });
            });
        }
        if (!this.options.getDisableAutoMetricsLogging()) {
            if (typeof document === 'undefined' ||
                !document ||
                typeof setTimeout === 'undefined' ||
                !setTimeout) {
                return;
            }
            const work = () => {
                setTimeout(() => {
                    this.logger.logAppMetrics(user);
                }, 1000);
            };
            if (document.readyState === 'complete') {
                work();
            }
            else {
                window.addEventListener('load', () => work());
            }
        }
        this.optionalLoggingSetup = true;
    }
    handleAppStateChange(nextAppState) {
        var _a;
        if (this.currentAppState === 'active' &&
            nextAppState.match(/inactive|background/)) {
            this.logger.flush(true);
        }
        else if (((_a = this.currentAppState) === null || _a === void 0 ? void 0 : _a.match(/inactive|background/)) &&
            nextAppState === 'active') {
            this.logger.sendSavedRequests();
        }
        this.currentAppState = nextAppState;
    }
    shouldTrimParam(entity, size) {
        if (entity == null)
            return false;
        if (typeof entity === 'string')
            return entity.length > size;
        if (typeof entity === 'object') {
            return JSON.stringify(entity).length > size;
        }
        if (typeof entity === 'number')
            return entity.toString().length > size;
        return false;
    }
    normalizeUser(user) {
        let userCopy = JSON.parse(JSON.stringify(user));
        userCopy = this.trimUserObjIfNeeded(userCopy);
        if (this.options.getEnvironment() != null) {
            // @ts-ignore
            userCopy.statsigEnvironment = this.options.getEnvironment();
        }
        return userCopy;
    }
    trimUserObjIfNeeded(user) {
        var _a, _b;
        if (user == null) {
            return {};
        }
        if (this.shouldTrimParam((_a = user.userID) !== null && _a !== void 0 ? _a : null, MAX_VALUE_SIZE)) {
            console.warn('User ID is too large, trimming to ' + MAX_VALUE_SIZE + 'characters');
            user.userID = (_b = user.userID) === null || _b === void 0 ? void 0 : _b.toString().substring(0, MAX_VALUE_SIZE);
        }
        if (this.shouldTrimParam(user, MAX_OBJ_SIZE)) {
            user.custom = {};
            if (this.shouldTrimParam(user, MAX_OBJ_SIZE)) {
                console.warn('User object is too large, only keeping the user ID.');
                user = { userID: user.userID };
            }
            else {
                console.warn('User object is too large, dropping the custom property.');
            }
        }
        return user;
    }
    ensureStoreLoaded() {
        if (!this.store.isLoaded()) {
            throw new Error('Call and wait for initialize() to finish first.');
        }
    }
}
exports.default = StatsigClient;
